import { DateTime } from 'luxon';
import { FC, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import Text from '@/components/Text';
import useQueueSettings from '@/hooks/dq/useSettings';
import { EditorialQueuePage } from '@/types/resources';

const Notification = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 12px 16px;
  border-radius: 4px;
  border: 0.5px solid ${({ theme }) => theme.colors.warning};
  background: #fff8e8;

  strong {
    font-weight: 700;
  }
`;

interface Props {
  activePage: EditorialQueuePage;
}

const formatRemaining = (dt: DateTime) => {
  const diff = dt.diffNow(['hours', 'minutes', 'seconds']).toObject();
  const hours = Math.max(0, Math.floor(diff.hours ?? 0));
  const minutes = Math.max(0, Math.floor(diff.minutes ?? 0));
  const seconds = Math.max(0, Math.floor(diff.seconds ?? 0));
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const PerformanceHoldNotification: FC<Props> = ({ activePage }) => {
  const settings = useQueueSettings(activePage);
  const theme = useTheme();
  const [, setTick] = useState(0);

  const until = settings?.performanceHoldUntil;
  const dt = until ? DateTime.fromISO(until) : null;
  const isActive =
    !!settings?.performanceHoldEnabled &&
    !!dt &&
    dt.isValid &&
    dt > DateTime.now();

  useEffect(() => {
    if (!isActive) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isActive]);

  if (!isActive || !dt) {
    return null;
  }

  const concurrents = settings?.performanceHoldConcurrents;

  return (
    <Notification>
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="7.5" fill={theme.colors.warning} />
        <path
          d="M7.25 5.73529V4.5H8.75V5.73529H7.25ZM7.25 11.5V6.55882H8.75V11.5H7.25Z"
          fill={theme.colors.white}
        />
      </svg>
      <Text $type="captionSemibold">
        <strong>Queue is on hold — {formatRemaining(dt)}. </strong>
        Last distributed post is performing well
        {concurrents !== undefined && (
          <>
            {' '}
            — it had <strong>{concurrents}</strong> concurrent visitors
          </>
        )}
        .
      </Text>
    </Notification>
  );
};

export default PerformanceHoldNotification;
